import React from "react";
import { injectIntl } from "react-intl";
import { withApollo } from "react-apollo";
import { setIn } from "final-form";
import { Form as FinalForm } from "react-final-form";
import arrayMutators from "final-form-arrays";

// Ramda imports
import curry from "ramda/src/curry";
import map from "ramda/src/map";
import is from "ramda/src/is";
import forEach from "ramda/src/forEach";

import { localize, recordDiff } from "cci-utility";

/**
 * Unflattens the Yup errors into the data shape.
 * 
 * For example, Yup returns:
 * [
 *   {
 *     ...,
 *     path: "userId",
 *     type: "required",
 *     message: "userId is a required field"
 *   }
 * ]
 * 
 * This function would return:
 * {
 *  "userId": "requiredFieldError"
 * }
 * 
 * OR
 * [
 *   {
 *     ...,
 *     path: "userId",
 *     type: undefined,
 *     message: "invalidEmailFieldError"
 *   }
 * ]
 * 
 * This function would return:
 * {
 *  "userId": "invalidEmailFieldError"
 * }
 */
const unflattenYupError = errors => {
  let result = {};

  forEach(error => {
    console.log('error type ===>', error.type);
    console.log('error      ===>', error);
    result = setIn(
      result,
      error.path,
      error.message.includes(error.params.path)
        && error.type == "required"
        && error.message.includes("is a required field")
        ? "requiredFieldError"
        : error.message || "requiredFieldError"
    );
  }, errors);

  return result;
};

/**
 * Curried function that localizes an error message, so that the intl prop does not have to be passed in each time.
 */
const localizeError = curry((intl, id) => {
  if (id) {
    return is(Object, id) ? map(localizeError(intl), id) : localize(intl, { id });
  }
  return;
});

/**
 * Special version of Final Form that allows to either submit the data to a GraphQL mutation or a custom function.
 * 
 * Prior to any data being sent to the server, Yup is used to validate the data in the form.
 * 
 * Props:
 * - mutation: GraphQL mutation used to create or update the data on the server.
 * - mutationName: Name of the mutation used to extract the data from the server's response.
 * - onSubmit: Function used to send the data to the server, you can either use this or mutation but not both.
 * - validationSchema: Yup schema to validate the form before submitting the data to the server.
 * - delta: Indicates if only the data that has changed should be sent to the server. Defaults to true.
 * - initialValues: Data to load the form with, prior to the user making any changes. This can be used to fetch data from the server and preload it into the form so the user can change it.
 * - objectWrapper: Key name to wrap the form values under before sending as a variable to the mutation. For example, wrapping all of the form values in an "office" object, and then passing that "office" object as a variable to the mutation.
 * - showMessage: Function used to show a message to the user, this is passed on in the Apollo context so that the mutation can show any message necessary (for the login mostly).
 * - onCompleted: Function called when the data has been stored successfully on the server. This function is called with the first parameter being the data returned by the server.
 * - onError: Function called when an error has occurred during mutation. This function is called with the first parameter being the errors returned by the server.
 * - beforeMutate: Function called before the mutate function is called.
 * - afterMutate: Function called after the mutate function is called.
 * - refetchQueries: Queries that must be refetched when the mutation completes successfully.
 * - onValidationError: Callback to execute when Yup validation fails.
 */
class Form extends React.Component {

  constructor() {
    super();

    /**
     * Context that will be passed into Yup.
     */
    this.validationContext = {};

    /**
     * Additional variables that will be passed to the mutation.
     */
    this.variables = {};

    this.interceptSubmit = this.interceptSubmit.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
  }

  /**
   * Our own version of Final Form's handleSubmit. The difference between this and Final Form's is
   * that we accept to override the form's submit action and the Yup validation context.
   */
  interceptSubmit(formOptions, { submit, validationContext, variables } = {}) {
    const { mutation, onSubmit } = this.props;

    // Context for yup, which lets it configure certain validations based on the context
    this.validationContext = validationContext || {};

    /*
     * Determines the action that will submit the form.
     * 
     * This is done as follows:
     * 1. Checks the mutation object passed in the handleSubmit function.
     * 2. Checks onSubmit function passed in the handleSubmit function.
     * 3. Checks the mutation object passed in the Form prop.
     * 4. Checks the onSubmit function passed in the Form prop.
     */
    this.submit = (submit || {}).mutation || (submit || {}).onSubmit || mutation || onSubmit;

    this.variables = variables;

    // Call Final Form's version of handleSubmit
    formOptions.handleSubmit();
  }

  /**
   * Handle's the GraphQL mutation response to update Final Form's form state so that any server side error is shown to the user.
   */
  handleMutationResponse({ data, errors }, callback, onCompleted, onError, form) {
    const { mutationName } = this.props;

    if (errors && errors.length > 0) {
      // There was a server-side error, so we handle them here
      errors.forEach(function (error) {
        if (error.errorType === "ValidationError") {
          /*
           * JPA Validation failed on the server side so we update the fields with the errors.
           *
           * Server returns an object "extensions" with the key being the field name and the value being the error message to show.
           */
          callback(error.extensions);
          onError(error.extensions);
        } else {
          console.warn(`Handle server side error "${error.errorType}".`, error);
        }
      });
    } else {
      callback();
      onCompleted(mutationName ? data[mutationName] : data, form);
    }
  }

  /**
   * Runs the submit action determined by interceptSubmit, see that function
   * for more detail.
   */
  runSubmit(values, form, callback, finalFormComplete) {
    const { submit, props } = this,
      {
        delta,
        client,
        initialValues,
        objectWrapper,
        showMessage,
        onCompleted = () => { },
        onError = () => { },
        beforeMutate = () => { },
        afterMutate = () => { },
        refetchQueries,
        optimisticResponse,
        update,
        onPressMock,
        // TODO The following is a temporary prop, We might delete it when all saved records are correctly checked in the delta logic.
        checkVersionDelta = true
      } = props;

    // TODO It might make sense to move this version logic somewhere else as this is a Form and this is more specific to an Inspect
    // We check for the version field because if this is a new record, it cannot be delta
    let sendData
    if (checkVersionDelta) {
      sendData = values.version && delta ? recordDiff(initialValues || {}, values) : values;
    } else {
      sendData = delta ? recordDiff(initialValues || {}, values) : values;
    }

    if (objectWrapper) {
      sendData = { [objectWrapper]: { ...sendData } };
    }

    if (is(Function, submit)) {
      // Form must be submitted by a function, so we call it here.
      submit(values, form, callback);
    } else if (is(Object, submit)) {
      // When we have an object we assume a GraphQL mutation, it must have the options sent to the mutation function: https://www.apollographql.com/docs/react/api/apollo-client.html#ApolloClient.mutate

      const options = {
        mutation: submit.mutation || submit,
        errorPolicy: "all",
        variables: { ...sendData, ...submit.variables, ...this.variables },
        context: { ...submit.context, callback, showMessage },
        refetchQueries,
        optimisticResponse,
        update
      };

      beforeMutate({ initialValues, options });

      client.mutate(options)
        .then(response => this.handleMutationResponse(response, callback, onCompleted, onError, form))
        .catch(error => {
          console.warn('form mutate error: ', error, error.graphQLErrors, error.networkError, error.operation, error.response)
          onError(error.message)
          finalFormComplete(error)
          // onPressMock()
        });

      afterMutate({ initialValues, options });
    }
  }

  /**
   * Fired when the form must be submitted to the server.
   * 
   * Here we validate the form with Yup.
   * We do this instead of using Final Form's validate function because we prefer that errors be shown
   * when submitting not when the form is first loaded and the user has not had a chance to enter anything into the form.
   */
  onSubmit(values, form, finalFormComplete) {
    const { intl, validationSchema, onBeforeSubmit, onValidationError } = this.props;

    if (validationSchema) {
      // Yup validation was specified, so we validate the form before submitting it.
      return new Promise(resolve => {
        validationSchema.validate(values, { abortEarly: false, context: this.validationContext }).then(
          () => {
            if (onBeforeSubmit) {
              // There is a custom validation function, so we execute that first
              const validationErrors = onBeforeSubmit(values);

              if (validationErrors) {
                resolve(validationErrors);
                onValidationError(form)

                return;
              }
            }

            this.runSubmit(values, form, resolve, finalFormComplete);
          },
          validationError => {
            resolve(map(localizeError(intl), unflattenYupError(validationError.inner)));
            onValidationError(form)
          }
        );
      });
    } else {
      // There is no validation so we submit right away
      return new Promise(resolve => this.runSubmit(values, form, resolve, finalFormComplete));
    }
  }

  render() {
    const { props, interceptSubmit, onSubmit } = this;

    return (
      <FinalForm
        subscription={{ submitting: true, pristine: true, invalid: true }}
        {...props}
        mutators={{ ...arrayMutators, ...props.mutators }}
        onSubmit={onSubmit}
        render={formOptions =>
          props.children({
            ...formOptions,
            handleSubmit: options => interceptSubmit(formOptions, options)
          })
        }
      />
    );
  }
}

Form.defaultProps = {
  delta: true,
  onValidationError: () => { }
}

export default injectIntl(withApollo(Form));
